import { memo, useState, useMemo, useEffect, useContext } from 'react';

import { CheckCircleFilled } from '@ant-design/icons';
import { Form, InputNumber, Tooltip } from 'antd';

import { StyledModal } from './style';

import { asyncNoPriceEnquirySave } from '@lcsc-ui/apis/order';
import SvgIcon from '@lcsc-ui/components/SvgIcon';
import { LayoutContext } from '@lcsc-ui/layouts/baseLayout/provider';

const PriceInquiryModal = (props) => {
  const { resourceData } = useContext(LayoutContext);

  const { showModal, changeShowModal, currentItem, purchaseCount, totalStockNumber, isOldBatch } = props;

  const [closeIconHoverStatus, setCloseIconHoverStatus] = useState(false);
  const [form] = Form.useForm();

  const [tipText, setTipText] = useState('');
  const [showTipText, setShowTipText] = useState(false);
  const [hasSubmit, setHasSubmit] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);

  useEffect(() => {
    form.setFieldsValue({ expectNumber: purchaseCount });
  }, [purchaseCount]);

  useEffect(() => {
    // 弹窗打开时，校验一次数量
    if (showModal && purchaseCount && purchaseCount !== 0) {
      inputBlur(purchaseCount);
    }
  }, [showModal]);

  const closeModal = () => {
    form?.resetFields();
    changeShowModal(false);
  };

  const handleFinish = async (values) => {
    if (btnLoading) return;
    setBtnLoading(true);
    let params;
    if (currentItem?.selfProductCode) {
      // 判断是否P转C
      params = {
        selfProductCode: currentItem?.selfProductCode,
        selfProductId: currentItem?.selfProductId,
        provider: currentItem?.lcBookProductVO?.provider,
        productCodeProvider: currentItem?.lcBookProductVO?.productCodeProvider,
        productCodeManufacturer: currentItem?.lcBookProductVO?.productCodeManufacturer,
        vendorCode: currentItem?.lcBookProductVO?.vendorCode,
      };
    } else if (currentItem?.provider) {
      // 存在provider即为订货或海外代购
      params = {
        provider: currentItem?.provider,
        productCodeProvider: currentItem?.productCodeProvider,
        productCodeManufacturer: currentItem?.productCodeManufacturer,
        vendorCode: currentItem?.vendorCode,
      };
    } else {
      // 现货
      params = {
        productId: currentItem?.productId,
        productCode: currentItem?.productCode,
      };
    }
    const res = await asyncNoPriceEnquirySave({
      ...params,
      ...values,
    }).finally(() => {
      setBtnLoading(false);
    });
    if (res?.code === 200) {
      setHasSubmit(true);
    }
  };

  const toStarPriceInquiry = () => {
    window.open(`${resourceData?.projectConfigVO?.lcscMemberUrl}/member/before/enquiry/list.html`, '_blank');
    closeModal();
  };

  const minBuyNumber = useMemo(() => {
    if (isOldBatch) {
      return currentItem?.convesionRatio;
    }
    return currentItem?.provider || currentItem?.selfProductCode
      ? currentItem?.minBuyNumber
      : currentItem?.minBuyNumber * currentItem?.theRatio;
  }, [currentItem, isOldBatch]);

  const stockNumber = useMemo(() => {
    if (isOldBatch) {
      return currentItem?.isDeposit === false ? currentItem?.oldBatchStockNumber : currentItem?.stockNumber;
    }
    return currentItem?.provider
      ? currentItem?.stockNumber
      : currentItem?.selfProductCode ||
          currentItem?.isMarkAutoOffShelves === 'yes' ||
          currentItem?.productCycle === 'stop_product'
        ? totalStockNumber
        : null;
  }, [currentItem, isOldBatch, totalStockNumber]);

  const inputBlur = (value) => {
    if (value === '') return;
    const { theRatio } = currentItem;
    let realCount = value;
    let content = '';
    if (minBuyNumber && (realCount < minBuyNumber || realCount < 1 || isNaN(realCount))) {
      content = `最小起订量为${minBuyNumber}，已为您修正购买数量`;
      realCount = minBuyNumber;
    }
    if (theRatio && realCount % theRatio !== 0) {
      content = `不符合递增量，已为您修正购买数量`;
      realCount = theRatio * Math.ceil(realCount / theRatio);
    }
    if (stockNumber !== null) {
      if (stockNumber < minBuyNumber) {
        content = `当前商品不可超库存购买`;
        realCount = stockNumber === 0 ? 0 : minBuyNumber;
      } else if (realCount > stockNumber) {
        content = `当前商品不可超库存购买, 已为您修正`;
        realCount = theRatio * Math.floor(stockNumber / theRatio);
      }
    }
    if (content) {
      setTipText(content);
      setShowTipText(true);
      setTimeout(() => {
        setShowTipText(false);
      }, 2000);
      form.setFieldsValue({ expectNumber: realCount });
    }
  };

  const CloseIcon = useMemo(() => {
    if (closeIconHoverStatus) {
      return 'modal-close-active';
    }
    return 'modal-close';
  }, [closeIconHoverStatus]);

  const renderSubmitSuccess = (
    <div className={'px-[24px] py-[19px]'}>
      <div className={'mb-[21px] flex items-center'}>
        <div className={'flex flex-1 items-center'}>
          <CheckCircleFilled
            style={{
              fontSize: '24px',
              color: '#52c41a',
            }}
          />
          <div className={'ml-[13px] text-[18px] font-bold text-[#000000]'}>提交询价成功</div>
        </div>
        <div
          className={'flex size-[16px] items-center justify-center'}
          onClick={closeModal}
          onMouseEnter={() => setCloseIconHoverStatus(true)}
          onMouseLeave={() => setCloseIconHoverStatus(false)}
        >
          <SvgIcon app="activity" iconName={CloseIcon} className={'size-[10px] cursor-pointer'} />
        </div>
      </div>
      <div className={'mb-[21px] text-[14px] text-[rgba(0,0,0,0.88)]'}>
        该商品已提交询价成功，点击
        <span className={'cursor-pointer px-[5px] text-[#0093E6]'} onClick={toStarPriceInquiry}>
          我的询价
        </span>
        跟进询价进度
      </div>
      <div className={'flex justify-end text-right'}>
        <button
          onClick={closeModal}
          className={
            'flex h-[32px] w-[90px] items-center justify-center rounded-[6px] bg-[#0093E6] text-[#FFFFFF] hover:opacity-80'
          }
        >
          知道了
        </button>
      </div>
    </div>
  );

  return (
    <>
      <StyledModal
        width={385}
        title=""
        closable={false}
        open={showModal}
        footer={null}
        keyboard={false}
        maskClosable={false}
        destroyOnHidden
        centered={true}
      >
        {hasSubmit ? (
          renderSubmitSuccess
        ) : (
          <>
            <div className={'mb-[21px] flex items-center px-[24px] pt-[19px]'}>
              <div className={'flex-1 text-[16px] font-bold text-[rgba(0,0,0,0.88)]'}>确认询价信息</div>
              <div
                className={'flex size-[16px] items-center justify-center'}
                onClick={closeModal}
                onMouseEnter={() => setCloseIconHoverStatus(true)}
                onMouseLeave={() => setCloseIconHoverStatus(false)}
              >
                <SvgIcon app="common" iconName={CloseIcon} className={'size-[10px]  cursor-pointer'} />
              </div>
            </div>
            <div
              className={
                'border-b border-solid border-[#F1F1F1] pb-[20px] pl-[10px] pr-[24px] text-[14px] text-[rgba(0,0,0,0.88)]'
              }
            >
              {currentItem?.productCode && !currentItem?.provider && (
                <div className={'mb-[15px] pl-[14px]'}>商品编号：{currentItem?.productCode}</div>
              )}
              <div className={'mb-[15px] pl-[14px]'}>
                型号：
                {currentItem?.provider ? currentItem?.productCodeManufacturer : currentItem?.productModel}
              </div>
              <div className={'mb-[15px] pl-[14px]'}>品牌：{currentItem?.productGradePlateName}</div>
              <div className={'mb-[15px] pl-[14px]'}>
                封装：
                {currentItem?.selfStandard || currentItem?.encapsulationModel || '-'}
              </div>
            </div>
            <div className={'px-[24px] py-[20px]'}>
              <Form form={form} autoComplete="off" preserve={false} onFinish={handleFinish} layout={'horizontal'}>
                <Tooltip
                  title={<span className={'text-[#000000]'}>{tipText}</span>}
                  open={showTipText}
                  color={'white'}
                  placement="top"
                  align={{
                    offset: [0, 0],
                  }}
                >
                  <Form.Item
                    label="数量"
                    name="expectNumber"
                    rules={[{ required: true, message: '数量不能为空' }]}
                    labelCol={{ style: { width: '70px' } }}
                  >
                    <InputNumber
                      min={0}
                      max={99999999}
                      maxLength={8}
                      placeholder={'需满足最小起订量，递增量'}
                      step={1}
                      className={'w-[228px]'}
                      controls={false}
                      onBlur={(event) => inputBlur(event.target.value)}
                    />
                  </Form.Item>
                </Tooltip>
                <Form.Item
                  label="期望单价"
                  name="expectPrice"
                  labelCol={{ style: { width: '70px' } }}
                  rules={[
                    {
                      validator(_, value) {
                        if (value === null || value === undefined || value === '') {
                          return Promise.resolve();
                        }
                        const regex = /^\d+(\.\d{1,4})?$/;
                        if (!regex.test(value) || value <= 0) {
                          return Promise.reject(new Error('请填写不超过4位小数的正数'));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <InputNumber max={99999999} placeholder={'单价（元）'} className={'w-[228px]'} controls={false} />
                </Form.Item>
                <Form.Item
                  label="期望交期"
                  name="expectAchieveDay"
                  labelCol={{ style: { width: '70px' } }}
                  rules={[
                    {
                      validator(_, value) {
                        if (value === undefined || value === null || value === '') {
                          return Promise.resolve();
                        }
                        if (!Number.isInteger(value)) {
                          return Promise.reject(new Error('请输入整数'));
                        }
                        if (value > 999 || value <= 0) {
                          return Promise.reject(new Error('请输入不超过3位数的正整数'));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <InputNumber placeholder={'期望交期（天）'} className={'w-[228px]'} controls={false} />
                </Form.Item>
                <div className={'flex justify-end text-right'}>
                  <button
                    onClick={closeModal}
                    className={
                      'mr-[10px] flex h-[32px] w-[65px] items-center justify-center rounded-[6px] border border-solid border-[rgba(0,0,0,0.15)] bg-[#FFFFFF] text-[rgba(0,0,0,0.88)] hover:border-[#0093E6] hover:text-[#0093E6]'
                    }
                  >
                    取消
                  </button>
                  <button
                    onClick={() => form.submit()}
                    className={
                      'flex h-[32px] w-[90px] items-center justify-center rounded-[6px] bg-[#0093E6] text-[#FFFFFF] hover:opacity-80'
                    }
                  >
                    提交询价
                  </button>
                </div>
              </Form>
            </div>
          </>
        )}
      </StyledModal>
    </>
  );
};

export default memo(PriceInquiryModal);
